import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { ArticlesOfSections } from "@/mk2/apps/strollers/containers/ArticlesOfSections/ArticlesOfSections";
import { strollersArticlesOfSectionsLoadTrigger } from "@/mk2/apps/strollers/containers/ArticlesOfSections/ArticlesOfSections.actions";
import {
    ARTICLES_OF_SECTIONS_MENU_SECTION_FILTER,
    STROLLERS_GUIDE_SECTIONS_CONFIG,
    STROLLERS_GUIDE_SECTION_RECENT,
} from "@/mk2/apps/strollers/containers/ArticlesOfSections/ArticlesOfSections.constants";
import reducer, {
    PerSectionState,
} from "@/mk2/apps/strollers/containers/ArticlesOfSections/ArticlesOfSections.reducers";
import saga from "@/mk2/apps/strollers/containers/ArticlesOfSections/ArticlesOfSections.sagas";
import { getStrollerArticlesState } from "@/mk2/apps/strollers/containers/ArticlesOfSections/ArticlesOfSections.selectors";
import { breadcrumbsArticleSections } from "@/mk2/apps/strollers/structuredData";
import { strollersArticlesUrl, strollersUrl } from "@/mk2/apps/strollers/urls";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, { PageRouteProps } from "@/mk2/pages/Page";
import { AppState, getRequestBaseUrl } from "@/mk2/reducers";
import { BlogPostEntity, BlogPostSchema } from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import { ARTICLES_TITLE, STROLLER_SEO_GENERAL_DESC } from "@/mk/autogenerated/translation";

interface OwnProps {}

interface StateProps {
    currentSectionSlug: string;
    sectionsData: {
        [sectionSlug: string]: PerSectionState;
    };
    allArticles: BlogPostEntity[];
    baseUrl: string;
}

interface DispatchProps {
    onLoad(section: string, after?: number, limit?: number);
    onSectionFilterMenuMore();
}

type RouteProps = PageRouteProps<{
    sectionSlug?: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ArticlesOfSectionsPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                routeParams: { sectionSlug: currentSectionSlug },
            } = ownProps;
            const articlesState = getStrollerArticlesState(state);
            const allArticles = getDenormalizedEntities<BlogPostEntity>(
                state,
                BlogPostSchema,
                articlesState.allArticleIds,
            );
            return {
                currentSectionSlug: !currentSectionSlug
                    ? STROLLERS_GUIDE_SECTION_RECENT
                    : currentSectionSlug,
                sectionsData: articlesState.sectionsData,
                allArticles,
                baseUrl: getRequestBaseUrl(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollersArticlesOfSectionsLoadTrigger,
            onSectionFilterMenuMore: () => menuToggle(ARTICLES_OF_SECTIONS_MENU_SECTION_FILTER),
        };
    }

    public getMetaTitle(props: Props): string {
        return ARTICLES_TITLE;
    }

    public getMetaDescription(props: Props): string {
        return STROLLER_SEO_GENERAL_DESC;
    }

    public render(props: Props): false | JSX.Element {
        return <ArticlesOfSections {...props} />;
    }

    public getHeaderTitle(
        props: OwnProps & RouteProps & StateProps & DispatchProps,
    ): string | null {
        return ARTICLES_TITLE;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(
        props: OwnProps & RouteProps & StateProps & DispatchProps,
    ): string | null {
        const {
            routeParams: { sectionSlug: currentSectionSlug },
        } = props;
        return !currentSectionSlug ? strollersUrl : strollersArticlesUrl;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.strollers.articles", reducer);
        store.injectSaga("containers.strollers.articles", saga);
    }

    public getStructuredData(props: Props): object[] {
        const {
            routeParams: { sectionSlug: currentSectionSlug },
        } = props;
        return currentSectionSlug
            ? [
                  breadcrumbsArticleSections(
                      props.baseUrl,
                      currentSectionSlug,
                      STROLLERS_GUIDE_SECTIONS_CONFIG[currentSectionSlug].label,
                  ),
              ]
            : [breadcrumbsArticleSections(props.baseUrl)];
    }
}
